import React, { useMemo } from 'react';
import {
  ClockOutlined,
  DiscountUniversal,
  MapOutlined,
  UserGroupOutlined,
} from '@duxton-web/icons';
import { useParams } from 'react-router-dom';

import AssignmentStrategyOption from './assignment-strategy-option';
import {
  ExperimentAssignmentStrategy,
  getAssignmentStrategy,
  getVariables,
  isPercentageSplitStrategy,
  isTimeSliceStrategy,
  isViewMode,
} from '@/features/experiment/constants';
import {
  useExperimentContext,
  useExperimentDispatchContext,
} from '@/features/experiment/contexts/experiment-context';
import { updateAssignmentStrategy } from '@/features/experiment/store/actions/experiment/action-creators';
import Service from '@/lib/App';

const extraTimeSliceMenu = [
  {
    key: ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
    title: 'Balanced Shuffle (Recommended)',
    subtitle: 'Ensure equally balanced representation of all treatments.',
  },
  {
    key: ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
    title: 'Sequential (Legacy)',
    subtitle:
      'The sequence will follow the treatments (Control, A, B, Control, ...)',
  },
];

const AssignmentStrategyOptions = ({
  personalizeByCountry,
}: {
  personalizeByCountry: boolean;
}) => {
  const params = useParams<{ experimentName: string; action: string }>();
  const { action } = params;
  const readOnly = isViewMode(action);

  const experimentContext = useExperimentContext();
  const { experiment, configurations } = experimentContext;
  const experimentDispatch = useExperimentDispatchContext();
  const experimentStrategy = getAssignmentStrategy(configurations);
  const variables = getVariables(configurations);

  const experimentDomain = experiment?.domain;
  const availableStrategies = useMemo(() => {
    let strategies = Service.cache.getAllStrategies();
    const domainlessFeatureFlag = Service.cache.isFeatureFlagOn('domainless');
    if (experimentDomain && !domainlessFeatureFlag) {
      return strategies.filter(
        (strategy) =>
          strategy?.allowedDomains &&
          strategy?.allowedDomains?.includes(experimentDomain),
      );
    }
    return strategies;
  }, [experimentDomain]);

  const availableStrategieNames = availableStrategies.map(
    (strategy) => strategy.name,
  );

  const showPercentageSplitButton =
    (availableStrategieNames.some((strategyName) =>
      [
        ExperimentAssignmentStrategy.UNIFORM_CHOICE,
        ExperimentAssignmentStrategy.WEIGHTED_CHOICE,
      ].includes(strategyName),
    ) &&
      !readOnly) ||
    (readOnly && isPercentageSplitStrategy(experimentStrategy));

  const showTimeSliceButton =
    (availableStrategieNames.some((strategyName) =>
      [
        ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
        ExperimentAssignmentStrategy.TIME_SLICE,
        ExperimentAssignmentStrategy.TIME_SLICE_V2,
        ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
        ExperimentAssignmentStrategy.TIME_SLICE_GROUPED,
      ].includes(strategyName),
    ) &&
      !readOnly &&
      !personalizeByCountry) ||
    (readOnly &&
      isTimeSliceStrategy(experimentStrategy) &&
      !personalizeByCountry);

  const showExplicitGroupButton =
    (availableStrategieNames.includes(
      ExperimentAssignmentStrategy.EXPLICIT_GROUP,
    ) &&
      !readOnly) ||
    (readOnly &&
      experimentStrategy === ExperimentAssignmentStrategy.EXPLICIT_GROUP);

  const showGeoTimeSliceShuffleButton =
    readOnly &&
    experimentStrategy === ExperimentAssignmentStrategy.GEO_TIME_SLICE_SHUFFLE;

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      {showPercentageSplitButton && (
        <AssignmentStrategyOption
          icon={<DiscountUniversal />}
          title="Percentage Split"
          subtitle="Each bucket has a same or different weight."
          selected={isPercentageSplitStrategy(experimentStrategy)}
          onSelect={() => {
            experimentDispatch(
              updateAssignmentStrategy(
                ExperimentAssignmentStrategy.UNIFORM_CHOICE,
              ),
            );
          }}
          disabled={variables.length === 0 || readOnly}
        />
      )}
      {showTimeSliceButton && (
        <AssignmentStrategyOption
          icon={<ClockOutlined />}
          title={
            availableStrategieNames.includes(
              ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
            )
              ? 'Time Slice (Balanced Shuffle)'
              : 'Time Slice (Sequential)'
          }
          subtitle="Specify a period of time for each variant."
          selected={isTimeSliceStrategy(experimentStrategy)}
          onSelect={() => {
            // if (timeSliceType) {
            //   if (
            //     timeSliceType ===
            //       ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE &&
            //     treatments.length > BALANCED_SHUFFLE_TIME_SLICE_MAX_TREATMENTS
            //   ) {
            //     showBalancedShuffleTimeSliceSwitchWarning(timeSliceType);
            //     return;
            //   }
            //   handleSelectStrategy(timeSliceType);
            //   setTimeSliceType(timeSliceType);
            //   return;
            // }
            if (
              availableStrategieNames.includes(
                ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
              )
            ) {
              experimentDispatch(
                updateAssignmentStrategy(
                  ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
                ),
              );
              return;
            }
            experimentDispatch(
              updateAssignmentStrategy(
                ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
              ),
            );
          }}
          timeSliceType={experimentStrategy}
          extraMenu={
            availableStrategieNames.includes(
              ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
            ) && extraTimeSliceMenu
          }
          onSelectExtraItem={(strategy) => {
            // if (
            //   strategy === ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE &&
            //   treatments.length > BALANCED_SHUFFLE_TIME_SLICE_MAX_TREATMENTS
            // ) {
            //   showBalancedShuffleTimeSliceSwitchWarning(strategy);
            //   return;
            // }
            experimentDispatch(updateAssignmentStrategy(strategy));
          }}
          disabled={variables.length === 0 || readOnly}
          selectedStrategy={experimentStrategy}
        />
      )}
      {showExplicitGroupButton && (
        <AssignmentStrategyOption
          icon={<UserGroupOutlined />}
          title="Explicit Group"
          subtitle="Specify a group for each variant."
          selected={
            experimentStrategy === ExperimentAssignmentStrategy.EXPLICIT_GROUP
          }
          onSelect={() => {
            experimentDispatch(
              updateAssignmentStrategy(
                ExperimentAssignmentStrategy.EXPLICIT_GROUP,
              ),
            );
          }}
          disabled={variables.length === 0 || readOnly}
        />
      )}
      {showGeoTimeSliceShuffleButton && (
        <AssignmentStrategyOption
          disabled={readOnly}
          title="Geo Time Slice Shuffle"
          subtitle="Shuffle based on geo regions"
          selected={
            experimentStrategy ===
            ExperimentAssignmentStrategy.GEO_TIME_SLICE_SHUFFLE
          }
          icon={<MapOutlined />}
        />
      )}
    </div>
  );
};

export default AssignmentStrategyOptions;
